import { create } from "zustand";
import { devtools } from "zustand/middleware";

export type VoiceRecorderState = {
  recording: boolean;
  recordTime: number;
  voiceBlob: Blob | null;
  recordingCanceled: boolean;
};

export type VoiceRecorderActions = {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  cancelRecording: () => void;
  resumeRecording: () => Promise<void>;
  sendVoice: () => void;
  reset: () => void;
  setOnSendVoice: (fn: (file: File) => void) => void;
  setDisabled: (disabled: boolean) => void;
};

// добавляем внутренние поля, которые ипользуются в логике
type VoiceRecorderInternals = {
  audioChunks: Blob[];
  savedChunks: Blob[];
  streamRef: MediaStream | null;
  timerRef: number | null;
  mediaRecorder: MediaRecorder | null;
  mergeBlobs: (blobs: Blob[]) => Promise<Blob>;
  handleRecorderStop: () => () => void;
};

export type VoiceRecorderStore = VoiceRecorderState &
  VoiceRecorderActions &
  VoiceRecorderInternals & {
    onSendVoice: (file: File) => void;
    disabled: boolean;
  };

const createVoiceRecorderStore = (set: any, get: any) => ({
  // State
  recording: false,
  recordTime: 0,
  voiceBlob: null,
  recordingCanceled: false,

  // Config
  onSendVoice: (file: File) => {
    console.warn("onSendVoice not set", file);
  },
  disabled: false,

  // Internals
  audioChunks: [],
  savedChunks: [],
  streamRef: null,
  timerRef: null,
  mediaRecorder: null,

  // mergeBlobs
  mergeBlobs: async (blobs: Blob[]): Promise<Blob> => {
    if (blobs.length === 1) return blobs[0];

    const buffers = await Promise.all(blobs.map((b) => b.arrayBuffer()));
    const audioCtx = new (window.AudioContext ||
      (window as any).webkitAudioContext)();
    const decoded = await Promise.all(
      buffers.map((buf) => audioCtx.decodeAudioData(buf))
    );

    const sampleRate = decoded[0].sampleRate;
    const totalLength = decoded.reduce((sum, b) => sum + b.length, 0);
    const mergedBuffer = audioCtx.createBuffer(1, totalLength, sampleRate);

    let offset = 0;
    decoded.forEach((buf) => {
      mergedBuffer.getChannelData(0).set(buf.getChannelData(0), offset);
      offset += buf.length;
    });

    function encodeWAV(audioBuffer: AudioBuffer): Blob {
      const numChannels = audioBuffer.numberOfChannels;
      const sampleRate = audioBuffer.sampleRate;
      const format = 1; // PCM
      const bitsPerSample = 16;

      const samples = audioBuffer.getChannelData(0);
      const buffer = new ArrayBuffer(44 + samples.length * 2);
      const view = new DataView(buffer);

      function writeString(view: DataView, offset: number, str: string) {
        for (let i = 0; i < str.length; i++) {
          view.setUint8(offset + i, str.charCodeAt(i));
        }
      }

      let offset = 0;

      writeString(view, offset, "RIFF");
      offset += 4;
      view.setUint32(offset, 36 + samples.length * 2, true);
      offset += 4;
      writeString(view, offset, "WAVE");
      offset += 4;
      writeString(view, offset, "fmt ");
      offset += 4;
      view.setUint32(offset, 16, true);
      offset += 4;
      view.setUint16(offset, format, true);
      offset += 2;
      view.setUint16(offset, numChannels, true);
      offset += 2;
      view.setUint32(offset, sampleRate, true);
      offset += 4;
      view.setUint32(
        offset,
        (sampleRate * numChannels * bitsPerSample) / 8,
        true
      );
      offset += 4;
      view.setUint16(offset, (numChannels * bitsPerSample) / 8, true);
      offset += 2;
      view.setUint16(offset, bitsPerSample, true);
      offset += 2;
      writeString(view, offset, "data");
      offset += 4;
      view.setUint32(offset, samples.length * 2, true);
      offset += 4;

      let pos = 44;
      for (let i = 0; i < samples.length; i++) {
        const s = Math.max(-1, Math.min(1, samples[i]));
        view.setInt16(pos, s < 0 ? s * 0x8000 : s * 0x7fff, true);
        pos += 2;
      }

      return new Blob([view], { type: "audio/wav" });
    }

    return encodeWAV(mergedBuffer);
  },

  // Actions
  setOnSendVoice: (fn: (file: File) => void) => set({ onSendVoice: fn }),
  setDisabled: (disabled: boolean) => set({ disabled }),

  reset: () =>
    set((state: VoiceRecorderStore) => {
      if (state.timerRef) {
        clearInterval(state.timerRef);
      }
      if (state.streamRef) {
        state.streamRef.getTracks().forEach((t) => t.stop());
      }

      return {
        recording: false,
        recordTime: 0,
        voiceBlob: null,
        recordingCanceled: false,
        audioChunks: [],
        savedChunks: [],
        streamRef: null,
        timerRef: null,
        mediaRecorder: null,
      };
    }),

  handleRecorderStop: () => {
    return () => {
      const state = get() as VoiceRecorderStore;
      const newBlob = new Blob(state.audioChunks, { type: "audio/webm" });
      const updatedSavedChunks = [...state.savedChunks, newBlob];

      // Асинхронно мерджим и обновляем blob
      state.mergeBlobs(updatedSavedChunks).then((finalBlob) => {
        set({ voiceBlob: finalBlob, savedChunks: updatedSavedChunks });
      });

      set({
        audioChunks: [],
        recording: false,
        mediaRecorder: null,
      });

      if (state.timerRef) {
        clearInterval(state.timerRef);
        set({ timerRef: null });
      }

      if (state.streamRef) {
        state.streamRef.getTracks().forEach((t) => t.stop());
        set({ streamRef: null });
      }
    };
  },

  startRecording: async () => {
    const state = get() as VoiceRecorderStore;
    if (state.disabled) return;

    try {
    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm'

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, {mimeType});

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          set((s: VoiceRecorderStore) => ({
            audioChunks: [...s.audioChunks, e.data],
          }));
        }
      };

      recorder.onstart = () => console.debug("[recorder] started");
      recorder.onerror = (err) => console.error("[recorder] error", err);
      recorder.onstop = state.handleRecorderStop();

      recorder.start();

      if (state.timerRef) clearInterval(state.timerRef);
      const timerRef = window.setInterval(() => {
        set((s: VoiceRecorderStore) => ({ recordTime: s.recordTime + 100 }));
      }, 100);

      set({
        mediaRecorder: recorder,
        recording: true,
        recordingCanceled: false,
        streamRef: stream,
        timerRef,
      });
    } catch (err) {
      console.error("[recorder] Mic access error:", err);
    }
  },

  resumeRecording: async () => {
    const state = get() as VoiceRecorderStore;
    if (state.disabled) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm'

      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          set((s: VoiceRecorderStore) => ({
            audioChunks: [...s.audioChunks, e.data],
          }));
        }
      };

      recorder.onstart = () => console.debug("[recorder] resumed");
      recorder.onerror = (err) => console.error("[recorder] resume error", err);
      recorder.onstop = state.handleRecorderStop();

      recorder.start();

      if (state.timerRef) clearInterval(state.timerRef);
      const timerRef = window.setInterval(() => {
        set((s: VoiceRecorderStore) => ({ recordTime: s.recordTime + 100 }));
      }, 100);

      set({
        mediaRecorder: recorder,
        recording: true,
        streamRef: stream,
        timerRef,
        audioChunks: [],
      });
    } catch (err) {
      console.error("[recorder] Resume error:", err);
    }
  },

  stopRecording: (send?: string) => {
    const state = get() as VoiceRecorderStore;
    if (!state.mediaRecorder) return;

    if (!state.mediaRecorder.onstop) {
      state.mediaRecorder.onstop = state.handleRecorderStop();
    }

    try {
      state.mediaRecorder.stop();

      if (send === "true") {
        setTimeout(() => {
          const sendAction = get().sendVoice;
          sendAction();
        }, 0);
      }
    } catch (err) {
      console.warn("[recorder] stop error", err);
      state.mediaRecorder.onstop?.(new Event("manual-stop"));
    }
  },

  cancelRecording: () => {
    const state = get() as VoiceRecorderStore;

    if (state.mediaRecorder) {
      state.mediaRecorder.onstop = () => {
        set({
          audioChunks: [],
          recording: false,
          recordTime: 0,
          mediaRecorder: null,
          recordingCanceled: true,
        });

        if (state.streamRef) {
          state.streamRef.getTracks().forEach((t) => t.stop());
          set({ streamRef: null });
        }

        if (state.timerRef) {
          clearInterval(state.timerRef);
          set({ timerRef: null });
        }
      };

      try {
        state.mediaRecorder.stop();
      } catch (err) {
        console.warn("[recorder] cancel error", err);
        state.mediaRecorder.onstop?.(new Event("manual-cancel"));
      }
    } else {
      state.reset();
      set({ recordingCanceled: true });
    }
  },

  sendVoice: () => {
    const state = get() as VoiceRecorderStore;
    if (state.savedChunks.length === 0 && !state.voiceBlob) return;

    const final =
      state.voiceBlob ?? new Blob(state.savedChunks, { type: "audio/webm" });
    const file = new File([final], `voice_${Date.now()}.wav`, {
      type: "audio/wav",
    });

    state.onSendVoice(file);

    set({
      savedChunks: [],
      voiceBlob: null,
      recordTime: 0,
    });
  },
});

export const useVoiceRecorderStore = create<VoiceRecorderStore>()(
  devtools(createVoiceRecorderStore, { name: "VoiceRecorderStore" })
);
